import { jsonify, sanitize } from "../TapiocaUtils";
export default class CbsClient {
  constructor(raw) {
    this.raw = raw;
  }
  /**
   * Adds the component to each of the given projects
   * For multiple components workflows see the related endpoint:
   * /components:add-to-projects
   */
  addToProjects(componentId, body) {
    const url = `/v1/components/${componentId}:add-to-projects`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Archives the given component, by soft removing the component from
   * existing projects.
   */
  archiveComponent(componentId, body) {
    const url = `/v1/components/${componentId}:archive`;
    return this.raw.post(url, jsonify(body));
  }
  callbackClone(body, params) {
    const url = {
      url: "/v1/components/clone_completion_callback",
      params: sanitize(params || {}),
    };
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Creates a component entry for the given account
   */
  createComponent(body) {
    const url = "/v1/components";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Creates a component or components from an asset or list of assets in a source project
   */
  createComponentsFromAssets(body) {
    const url = "/v1/components:create-from-assets";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Logical Delete of the given component, this will update the component state to DELETED.
   * It does not attempt to update any project usage.
   * note: This endpoint is designed for deleting FAILED components due to the
   * import form templates flow.
   */
  deleteComponent(componentId, body) {
    const url = `/v1/components/${componentId}:delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Duplicate a component based on the asset provided.
   */
  duplicateComponent(body) {
    const url = "/v1/components:duplicate-component";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Duplicate all components from the source project into the target project
   */
  duplicateToProject(body) {
    const url = "/v1/components:duplicate-to-project";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Filters the collection of components
   */
  filterComponents(params) {
    const url = {
      url: "/v1/components",
      params: sanitize(params || {}, {
        "filter[failed]": "csv",
        "filter[pending]": "csv",
        "filter[state]": "csv",
        sort: "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * Filters the collection of components through a POST request.
   * Use this endpoint for more complex requests where you need to pass
   * multiple values to parameters to be used.
   */
  filterComponentsCustom(body) {
    const url = "/v1/components:filter";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Gets the Component definitions for all component types.
   * Component definition is comprised a list of component names and the
   * corresponding list of attribute datatypes - on a per component type basis.
   */
  getComponentDefinitions(params) {
    const url = {
      url: "/v1/components/component-definitions",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Gets a paginated list of projects using the given component
   * For multiple components workflows see the related endpoint:
   * /components:usage
   */
  getComponentUsage(componentId, params) {
    const url = {
      url: `/v1/components/${componentId}/usage`,
      params: sanitize(params || {}, {
        "filter[failed]": "csv",
        "filter[pending]": "csv",
      }),
    };
    return this.raw.get(url);
  }
  /**
   * Gets the component library
   */
  getLibrary(accountUid) {
    const url = `/v1/library/${accountUid}`;
    return this.raw.get(url);
  }
  /**
   * Gets a paginated dictionary of project usages for the given components.
   * The key is the component id and the value is a (potentially empty) list of project usages for
   * that component.
   * For simple use cases like getting usage of one component see the related endpoint:
   * /components/<uuid:componentId>/usage
   */
  getMultipleComponentsUsage(body) {
    const url = "/v1/components:usage";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Adds multiple components to each of the given projects.
   * For simple use cases like adding one component to multiple
   * projects see the related endpoint:
   * /components/<uuid:componentId>:add-to-projects"
   */
  multipleAddToProjects(body) {
    const url = "/v1/components:add-to-projects";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Removes multiple components from each of the given projects.
   * For simple use cases like removing one component from multiple
   * projects see the related endpoint:
   * /components/<uuid:componentId>:remove-from-projects"
   */
  multipleRemoveFromProjects(body) {
    const url = "/v1/components:remove-from-projects";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Removes the component from each of the given projects
   * For multiple components workflows see the related endpoint:
   * /components:remove-from-projects
   */
  removeFromProjects(componentId, body) {
    const url = `/v1/components/${componentId}:remove-from-projects`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Restores the given component, by undoing the soft removal of the
   * component from existing projects.
   */
  restoreComponent(componentId, body) {
    const url = `/v1/components/${componentId}:restore`;
    return this.raw.post(url, jsonify(body));
  }
  systemInfo() {
    const url = "/v1/system/info";
    return this.raw.get(url);
  }
  /**
   * Update a component entry
   */
  updateComponent(componentId, body) {
    const url = `/v1/components/${componentId}`;
    return this.raw.post(url, jsonify(body));
  }
}
